import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, UserRound } from "lucide-react";
import { toast } from "sonner";

export default function B2CStudents() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [dupes, setDupes] = useState([]);
  const [checking, setChecking] = useState(false);
  const nav = useNavigate();
  const load = () => api.get(`/b2c/students?q=${encodeURIComponent(q)}`).then(r=>setRows(r.data));
  useEffect(() => { load(); }, [q]); // eslint-disable-line

  // Debounced duplicate check as user types uci/email/phone
  useEffect(() => {
    if (!open) { setDupes([]); return; }
    const uci = (f.uci||"").trim(); const email = (f.email||"").trim(); const phone = (f.phone||"").trim();
    if (!uci && !email && !phone) { setDupes([]); return; }
    setChecking(true);
    const h = setTimeout(async () => {
      try {
        const qs = new URLSearchParams();
        if (uci) qs.set("uci", uci); if (email) qs.set("email", email); if (phone) qs.set("phone", phone);
        const r = await api.get(`/b2c/students/check-duplicate?${qs}`);
        setDupes(r.data.duplicates || []);
      } catch {} setChecking(false);
    }, 350);
    return () => clearTimeout(h);
  }, [f.uci, f.email, f.phone, open]);

  const save = async () => {
    if (!f.full_name) return toast.error("Full name required");
    if (dupes.length > 0) return toast.error("Please resolve the duplicate warning before saving");
    try { const r = await api.post("/b2c/students", f); toast.success("Student created"); setOpen(false); setF({}); nav(`/b2c/students/${r.data.id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="heading-font text-3xl font-bold text-slate-900">Students</h1><p className="text-sm text-slate-500 mt-1">{rows.length} student{rows.length!==1?"s":""}</p></div>
        <Button data-testid="add-student-btn" onClick={()=>setOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-2"/>Add Student</Button>
      </div>
      <Input data-testid="students-search" placeholder="Search by name, email, phone, or ID…" value={q} onChange={e=>setQ(e.target.value)} className="max-w-md bg-white"/>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="text-left px-4 py-3">ID</th><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Phone</th><th className="text-left px-4 py-3">Country</th></tr></thead>
          <tbody>
            {rows.map(s => (
              <tr key={s.id} data-testid={`student-row-${s.student_id}`} onClick={()=>nav(`/b2c/students/${s.id}`)}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                <td className="px-4 py-3 mono text-xs">{s.student_id}</td>
                <td className="px-4 py-3 font-medium flex items-center gap-2"><UserRound className="w-4 h-4 text-slate-400" />{s.full_name}</td>
                <td className="px-4 py-3 text-slate-600">{s.email}</td>
                <td className="px-4 py-3">{s.phone}</td>
                <td className="px-4 py-3">{s.country}</td>
              </tr>
            ))}
            {rows.length===0 && <tr><td colSpan={5} className="text-center py-10 text-slate-500">No students yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle>New Student</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Full Name *</Label><Input data-testid="student-name-input" value={f.full_name||""} onChange={e=>setF({...f, full_name:e.target.value})}/></div>
            <div><Label>Email</Label><Input data-testid="student-email-input" value={f.email||""} onChange={e=>setF({...f, email:e.target.value})}/></div>
            <div><Label>Phone</Label><Input data-testid="student-phone-input" value={f.phone||""} onChange={e=>setF({...f, phone:e.target.value})}/></div>
            <div><Label>UCI (Unique Client ID)</Label><Input data-testid="student-uci-input" value={f.uci||""} onChange={e=>setF({...f, uci:e.target.value})}/></div>
            <div><Label>City</Label><Input value={f.city||""} onChange={e=>setF({...f, city:e.target.value})}/></div>
            <div><Label>Country</Label><Input value={f.country||""} onChange={e=>setF({...f, country:e.target.value})}/></div>
          </div>
          {(checking || dupes.length > 0) && (
            <div data-testid="dup-warning" className={`mt-3 p-3 rounded-lg border ${dupes.length>0 ? "border-red-300 bg-red-50 text-red-900" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
              {checking && dupes.length === 0 && <span className="text-xs">Checking for duplicates…</span>}
              {dupes.length > 0 && (<>
                <div className="text-sm font-semibold flex items-center gap-2">Possible duplicate{dupes.length>1?"s":""} — cannot save</div>
                <ul className="text-xs mt-2 space-y-1">
                  {dupes.map(d => (
                    <li key={d.id}>
                      <button onClick={()=>{ setOpen(false); nav(`/b2c/students/${d.id}`); }} className="underline">
                        {d.full_name} ({d.student_id})
                      </button> — {[d.uci && `UCI ${d.uci}`, d.email, d.phone].filter(Boolean).join(" · ")}
                    </li>
                  ))}
                </ul>
              </>)}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button data-testid="student-save-btn" onClick={save} disabled={dupes.length>0} className="bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
